import React, { useState } from "react";
import { Eye, EyeOff } from "lucide-react";

/**
 * PasswordInput — text input with eye toggle to show/hide value.
 * Use the same props you would use on a native <input>; pass `data-testid` for tests.
 * The toggle button gets `data-testid={`${dataTestid}-toggle`}` automatically.
 *
 * `variant` controls the underline colour scheme:
 *  - "light"  : dark text on ivory background (default — public forms, admin panels)
 *  - "dark"   : ivory text on navy background (used on the admin login page)
 */
export default function PasswordInput({
  variant = "light",
  className = "",
  ["data-testid"]: dataTestid,
  ...rest
}) {
  const [visible, setVisible] = useState(false);

  const isDark = variant === "dark";
  const base = isDark
    ? "w-full bg-transparent border-b border-ivory/25 py-2 pr-10 text-ivory focus:outline-none focus:border-brand transition-colors"
    : "w-full bg-transparent border-b border-ink/20 py-2 pr-10 focus:outline-none focus:border-brand transition-colors";
  const btnColor = isDark
    ? "text-ivory/60 hover:text-brand"
    : "text-ink/50 hover:text-brand";

  return (
    <div className="relative">
      <input
        {...rest}
        type={visible ? "text" : "password"}
        data-testid={dataTestid}
        className={`${base} ${className}`}
      />
      <button
        type="button"
        onClick={() => setVisible((v) => !v)}
        aria-label={visible ? "Masquer le mot de passe" : "Afficher le mot de passe"}
        data-testid={dataTestid ? `${dataTestid}-toggle` : undefined}
        className={`absolute right-0 bottom-2 ${btnColor}`}
        tabIndex={-1}
      >
        {visible ? <EyeOff size={16} /> : <Eye size={16} />}
      </button>
    </div>
  );
}
